import { ImportAlertProps } from "components/importer/ImportAlert";
import { Tenant, Location } from "types";

export interface FieldMetadata<T extends string> {
  field: T;
  required: boolean;
  type: "string" | "number" | "date" | "enum";
  category: "entity" | "vehicle" | "contact" | "location";
  specialOptions: Array<{
    value: string;
    label: string;
  }>;
  enumValues?: string[];
  validator: (value: string | undefined) => boolean;
  formatter?: (value: string) => string | null;
  alerts: ImportAlertProps[];
}

export type DataWidget =
  | "preview"
  | "generate"
  | "validate"
  | "combine"
  | "smartMap"
  | "tutorial"
  | "none";

export enum ImportWizardActionHandler {
  SAVE_COLUMN_MAPPINGS = "SAVE COLUMN MAPPINGS",
  CREATE_ENTITY = "CREATE ENTITY",
  SUBMIT_ENTITY_DATA = "SUBMIT ENTITY DATA",
  UNKNOWN = "UNKNOWN",
}

export interface SavedImportMapping {
  name: string;
  mappings: Record<string, string>;
}

export interface HANDLE_NEXT {
  type: WIZARD_ACTION.HANDLE_NEXT;
  payload?: null;
}

export interface HANDLE_BACK {
  type: WIZARD_ACTION.HANDLE_BACK;
  payload?: null;
}

export interface SET_MODIFIED_DATA {
  type: WIZARD_ACTION.SET_MODIFIED_DATA;
  payload: Record<string, string>[];
}

export interface SET_COLUMN_MAPPINGS {
  type: WIZARD_ACTION.SET_COLUMN_MAPPINGS;
  payload: Record<string, string>;
}

export interface SET_CSV_HEADERS {
  type: WIZARD_ACTION.SET_CSV_HEADERS;
  payload: string[];
}

export interface SET_HAS_VALID_CSV_DATA {
  type: WIZARD_ACTION.SET_HAS_VALID_CSV_DATA;
  payload: boolean;
}

export interface SET_HAS_VALID_COLUMN_MAPPINGS {
  type: WIZARD_ACTION.SET_HAS_VALID_COLUMN_MAPPINGS;
  payload: boolean;
}

export interface SET_FILE_NAME {
  type: WIZARD_ACTION.SET_FILE_NAME;
  payload: string;
}

export interface CONFIRM_MAPPING<F extends string> {
  type: WIZARD_ACTION.CONFIRM_MAPPING;
  payload: F;
}

export interface SET_FOCUSED_ROWS {
  type: WIZARD_ACTION.SET_FOCUSED_ROWS;
  payload: number[];
}

export interface SET_FAILED_IMPORTS<T> {
  type: WIZARD_ACTION.SET_FAILED_IMPORTS;
  payload: FailedImport<T>[];
}

export interface POP_FAILED_IMPORT {
  type: WIZARD_ACTION.POP_FAILED_IMPORT;
  payload?: null;
}

export interface SET_DISCARDED_ROWS {
  type: WIZARD_ACTION.SET_DISCARDED_ROWS;
  payload: number[];
}

export interface RESET_STATE {
  type: WIZARD_ACTION.RESET_STATE;
  payload?: null;
}

export interface UPDATE_FAILED_IMPORT<T> {
  type: WIZARD_ACTION.UPDATE_FAILED_IMPORT;
  payload: T;
}

export interface SET_CSV_USES_TEMPLATE {
  type: WIZARD_ACTION.SET_CSV_USES_TEMPLATE;
  payload: boolean;
}

export interface GENERATE_DATA<F extends string> {
  type: WIZARD_ACTION.GENERATE_DATA;
  payload: {
    field: F;
    value: string;
  };
}

export interface APPEND_COLUMN_MAPPING<F extends string> {
  type: WIZARD_ACTION.APPEND_COLUMN_MAPPING;
  payload: {
    systemField: F;
    csvField: string;
  };
}

export interface SET_SELECTED_SYSTEM_FIELD<F extends string | null> {
  type: WIZARD_ACTION.SET_SELECTED_SYSTEM_FIELD;
  payload: F | null;
}

export interface SET_SELECTED_CSV_FIELD<F extends string | null> {
  type: WIZARD_ACTION.SET_SELECTED_CSV_FIELD;
  payload: F | null;
}

export interface SELECT_NEXT_SYSTEM_FIELD {
  type: WIZARD_ACTION.SELECT_NEXT_SYSTEM_FIELD;
  payload?: null;
}

interface AUTOFILL_ADDRESS {
  type: WIZARD_ACTION.AUTOFILL_ADDRESS;
  payload?: null;
}

export interface SELECT_NEXT_CSV_FIELD {
  type: WIZARD_ACTION.SELECT_NEXT_CSV_FIELD;
  payload?: null;
}

export interface SELECT_PREVIOUS_SYSTEM_FIELD {
  type: WIZARD_ACTION.SELECT_PREVIOUS_SYSTEM_FIELD;
  payload?: null;
}

export interface SELECT_PREVIOUS_CSV_FIELD {
  type: WIZARD_ACTION.SELECT_PREVIOUS_CSV_FIELD;
  payload?: null;
}

export interface CLEAR_FIELD_MAPPING<F extends string> {
  type: WIZARD_ACTION.CLEAR_FIELD_MAPPING;
  payload: {
    systemField: F;
    csvField: string;
  };
}

export interface VALIDATE_ALL_MAPPED_FIELDS {
  type: WIZARD_ACTION.VALIDATE_ALL_MAPPED_FIELDS;
  payload?: null;
}

export interface DISCARD_DUPLICATE_ROWS {
  type: WIZARD_ACTION.DISCARD_DUPLICATE_ROWS;
  payload?: null;
}

export interface IMPORT_CSV_DATA {
  type: WIZARD_ACTION.IMPORT_CSV_DATA;
  payload: {
    fileName: string;
    results: Papa.ParseResult<Record<string, string>>;
  };
}

// TODO:  This needs typed
export interface IMPORT_SAVED_MAPPINGS {
  type: WIZARD_ACTION.IMPORT_SAVED_MAPPINGS;
  payload: Record<string, string>;
}

export interface SET_STRINGIFIED_IMPORTS {
  type: WIZARD_ACTION.SET_STRINGIFIED_IMPORTS;
  payload: string;
}

export interface SET_PREVIEW_IMPORTS<T> {
  type: WIZARD_ACTION.SET_PREVIEW_IMPORTS;
  payload: T[];
}

export interface MAP_COMBINED_FIELDS<F extends string> {
  type: WIZARD_ACTION.MAP_COMBINED_FIELDS;
  payload: {
    systemField: F;
    fields: { csvField: string; trailingCharacter: string }[];
  };
}

export interface SET_ENTITY_NAME {
  type: WIZARD_ACTION.SET_ENTITY_NAME;
  payload: string;
}

interface SET_PARSED_VEHICLE_DATA {
  type: WIZARD_ACTION.SET_PARSED_VEHICLE_DATA;
  payload: {
    csvHeader: string;
    parsedVehicleData: {
      color: string;
      make: string;
      model: string;
      year: string;
    }[];
  };
}

export interface SET_TENANT {
  type: WIZARD_ACTION.SET_TENANT;
  payload: Tenant;
}

export const enum WIZARD_ACTION {
  HANDLE_NEXT = "HANDLE_NEXT",
  HANDLE_BACK = "HANDLE_BACK",
  SET_MODIFIED_DATA = "SET_MODIFIED_DATA",
  SET_COLUMN_MAPPINGS = "SET_COLUMN_MAPPINGS",
  SET_CSV_HEADERS = "SET_CSV_HEADERS",
  SET_HAS_VALID_CSV_DATA = "SET_HAS_VALID_CSV_DATA",
  SET_HAS_VALID_COLUMN_MAPPINGS = "SET_HAS_VALID_COLUMN_MAPPINGS",
  SET_FILE_NAME = "SET_FILE_NAME",
  CONFIRM_MAPPING = "CONFIRM_MAPPING",
  SET_FOCUSED_ROWS = "SET_FOCUSED_ROWS",
  SET_FAILED_IMPORTS = "SET_FAILED_IMPORTS",
  POP_FAILED_IMPORT = "POP_FAILED_IMPORT",
  UPDATE_FAILED_IMPORT = "UPDATE_FAILED_IMPORT",
  SET_DISCARDED_ROWS = "SET_DISCARDED_ROWS",
  RESET_STATE = "RESET_STATE",
  SET_CSV_USES_TEMPLATE = "SET_CSV_USES_TEMPLATE",
  GENERATE_DATA = "GENERATE_DATA",
  APPEND_COLUMN_MAPPING = "APPEND_COLUMN_MAPPING",
  SET_SELECTED_SYSTEM_FIELD = "SET_SELECTED_SYSTEM_FIELD",
  SET_SELECTED_CSV_FIELD = "SET_SELECTED_CSV_FIELD",
  SELECT_NEXT_SYSTEM_FIELD = "SELECT_NEXT_SYSTEM_FIELD",
  SELECT_NEXT_CSV_FIELD = "SELECT_NEXT_CSV_FIELD",
  SELECT_PREVIOUS_SYSTEM_FIELD = "SELECT_PREVIOUS_SYSTEM_FIELD",
  SELECT_PREVIOUS_CSV_FIELD = "SELECT_PREVIOUS_CSV_FIELD",
  CLEAR_FIELD_MAPPING = "CLEAR_FIELD_MAPPING",
  VALIDATE_ALL_MAPPED_FIELDS = "VALIDATE_ALL_MAPPED_FIELDS",
  DISCARD_DUPLICATE_ROWS = "DISCARD_DUPLICATE_ROWS",
  IMPORT_CSV_DATA = "IMPORT_CSV_DATA",
  IMPORT_SAVED_MAPPINGS = "IMPORT_SAVED_MAPPINGS",
  SET_STRINGIFIED_IMPORTS = "SET_STRINGIFIED_IMPORTS",
  SET_PREVIEW_IMPORTS = "SET_PREVIEW_IMPORTS",
  MAP_COMBINED_FIELDS = "MAP_COMBINED_FIELDS",
  SET_ENTITY_NAME = "SET_ENTITY_NAME",
  SET_PARSED_VEHICLE_DATA = "SET_PARSED_VEHICLE_DATA",
  AUTOFILL_ADDRESS = "AUTOFILL_ADDRESS",
  SET_TENANT = "SET_TENANT",
}

export type ImportWizardAction<T, F extends string> =
  | HANDLE_NEXT
  | HANDLE_BACK
  | SET_COLUMN_MAPPINGS
  | SET_CSV_HEADERS
  | SET_MODIFIED_DATA
  | SET_HAS_VALID_CSV_DATA
  | SET_HAS_VALID_COLUMN_MAPPINGS
  | SET_FILE_NAME
  | CONFIRM_MAPPING<F>
  | SET_FOCUSED_ROWS
  | SET_FAILED_IMPORTS<T>
  | POP_FAILED_IMPORT
  | UPDATE_FAILED_IMPORT<T>
  | SET_DISCARDED_ROWS
  | RESET_STATE
  | SET_CSV_USES_TEMPLATE
  | GENERATE_DATA<F>
  | APPEND_COLUMN_MAPPING<F>
  | SET_SELECTED_SYSTEM_FIELD<F>
  | SET_SELECTED_CSV_FIELD<F>
  | SELECT_NEXT_SYSTEM_FIELD
  | SELECT_NEXT_CSV_FIELD
  | SELECT_PREVIOUS_SYSTEM_FIELD
  | SELECT_PREVIOUS_CSV_FIELD
  | CLEAR_FIELD_MAPPING<F>
  | VALIDATE_ALL_MAPPED_FIELDS
  | DISCARD_DUPLICATE_ROWS
  | AUTOFILL_ADDRESS
  | IMPORT_CSV_DATA
  | IMPORT_SAVED_MAPPINGS
  | SET_STRINGIFIED_IMPORTS
  | SET_PREVIEW_IMPORTS<T>
  | MAP_COMBINED_FIELDS<F>
  | SET_ENTITY_NAME
  | SET_PARSED_VEHICLE_DATA
  | SET_TENANT;

export interface GetNextUnconfirmedFieldArgs<F extends string> {
  fromField: F | null;
  allFields: F[];
  confirmedFields: F[];
}

export interface ImportWizardState<T extends object, F extends string> {
  //field type, object type (i.e. PermitField, Permit)
  entityName: string;
  activeStep: ImportStep;
  fileName: string;
  csvData: Record<string, string>[];
  modifiedData: Record<string, string>[];
  csvHeaders: string[];
  columnMappings: Record<string, string>;
  entityFields: F[];
  confirmedFields: F[];
  failedImports: FailedImport<T>[];
  failedImport: T | null;
  discardedRows: number[];
  tenant: Tenant | null;
  hasValidCsvData: boolean;
  hasValidColumnMappings: boolean;
  csvUsesTemplate: boolean;
  previewEntities: T[];
  stringifiedEntities: string;
  focusedRows: number[];
  selectedSystemField: F | null;
  selectedCsvField: string | null;
  fieldMetadata: Record<F, FieldMetadata<F>> | null;
  savedImportMappings: SavedImportMapping[];
  defaultAddress: Location;
}

export interface FailedImport<T> {
  entity: T;
  error: string;
  index: number;
}

export enum ImportStep {
  UPLOAD = "Define and Upload",
  MAP = "Map and Validate",
  PREVIEW = "Preview and Confirm",
  COMPLETE = "Complete",
}

export interface CreateEntityParams {
  row: Record<string, string>;
  mappings: Record<string, string>;
}

export interface IdentifyDuplicatesParams {
  columnMappings: Record<string, string>;
  data: Record<string, string>[];
}
